import React, { useRef, useEffect, useState } from 'react'
import { AdminTableProps } from '../../models'
import { useAppSelector } from '../../../../app/hooks'
import {
  selectedLmsAdminSearch,
  selectedLmsLocationSearch,
  selectedLmsStatus,
} from '../../lmsSlice'
import { getBedResults } from '../../utils'
import { create, menuValues, update } from '../../constants'
import { allocation } from '../../../bedManagement/modals'
import { Box, IconButton, useTheme } from '@mui/material'
import KeycloakService from '../../../../utils/keycloakService'
import DeleteOutlined from '@mui/icons-material/DeleteOutlined'
import { SUPER_ADMIN_DELETE } from '../../../../utils/roles'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined'
import '../../../administration/admin.scss'
import { Archive } from './Archive'
import {
  activeText,
  emptyString,
  inactiveText,
  pageSizeOptions,
} from '../../../Radiology/Constants/constants'
import { ColDef } from 'ag-grid-community'
import MedAgDataGrid from '../../../../components/MedAgDataGrid'
import useCustomStyles from '../../../../utils/hooks/CustomStylesHook'
import CustomHeader from '../../../Radiology/components/RadiologyModel/CustomHeader'
import { radiologyColor } from '../../../Radiology/Utils/RadiologyColors'
import { DeleteConfirmationDialog } from './DeleteConfirmationDialog'
import ProvenanceView from '../../../../components/ProvenanceView'
import { loading } from '../../../appointments/models'
const styles = () => ({
  centerHeader: {
    color: radiologyColor?.white,
    textAlign: 'center',
    '& .ag-header-cell-text': {
      flex: 1,
    },
  },
  nameButton: {
    backgroundColor: '#0000',
    border: 'none',
    color: 'blue',
    cursor: 'pointer',
    padding: '0px',
    textAlign: 'left',
    textDecoration: 'underline',
  },
  interpretationView: {
    color: 'blue',
    cursor: 'pointer',
  },
  dialogTitleText: {
    fontSize: '1rem',
    fontWeight: 500,
  },
  dialogContent: {
    padding: '8px 16px',
  },
  templateItem: {
    padding: '6px 8px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  templateText: {
    fontSize: '0.9rem',
  },
  noTemplatesText: {
    padding: '8px 0',
  },
  iconButtonContainer: {
    display: 'flex',
  },
  titleContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  listContainer: {
    padding: 0,
  },
})

const Beds = ({ data, onEdit }: AdminTableProps) => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const searchParam = useAppSelector(selectedLmsAdminSearch)
  const locationSearch = useAppSelector(selectedLmsLocationSearch)
  const status = useAppSelector(selectedLmsStatus)
  const [deleteDialog, setDeleteDialog] = useState<boolean>(false)
  const [bedData, setBedData] = useState<any>({})
  const [tableData, setTableData] = useState<any>(data)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(100)
  const [totalCount, setTotalCount] = useState(0)
  let filtered = getBedResults(tableData, searchParam, locationSearch)
  filtered = filtered?.filter((d: any) => {
    const bedStatus = d?.active ? activeText : inactiveText
    return status === bedStatus
  })
  const sampleTableContainerRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const resizeHandler = () => {
      const container = sampleTableContainerRef.current
      if (container) {
        const availableHeight: any =
          window.innerHeight - container.getBoundingClientRect().top
        container.style.maxHeight = `${availableHeight - 20}px`
      }
    }

    window.addEventListener('resize', resizeHandler)
    resizeHandler()

    return () => {
      window.removeEventListener('resize', resizeHandler)
    }
  }, [])

  function handleDeleteDialogOpen(d: any) {
    setDeleteDialog(true)
    setBedData(d)
  }

  function handleCloseDeleteDialog() {
    setDeleteDialog(false)
    setBedData(null)
  }

  useEffect(() => {
    setTableData(data)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, data])
  const renderName = (params: any) => {
    return (
      <button
        onClick={() => onEdit(params?.data?.id)}
        className={classes?.nameButton}
      >
        {params?.data?.bed_number}
      </button>
    )
  }
  const createProvenanceView = (params: any) => {
    return (
      <ProvenanceView
        mode={create}
        date={params?.data?.date_created ?? ''}
        name={params?.data?.created_by ?? '-'}
      />
    )
  }
  const updatedProvenanceView = (params: any) => {
    return (
      <ProvenanceView
        mode={update}
        date={params?.data?.date_changed ?? ''}
        name={params?.data?.updated_by ?? '-'}
      />
    )
  }
  const RenderActions = (params: any) => {
    return (
      <div className='set-action-icons'>
        {params?.data?.active ? (
          <IconButton
            title='Archive'
            size='small'
            onClick={() => Archive(params?.data, setTableData, menuValues?.Bed)}
            disabled={!KeycloakService?.hasRole([SUPER_ADMIN_DELETE])}
          >
            <ArchiveOutlinedIcon fontSize='small' />
          </IconButton>
        ) : (
          <IconButton
            title='Un-Archive'
            size='small'
            onClick={() => Archive(params?.data, setTableData, menuValues?.Bed)}
            disabled={!KeycloakService?.hasRole([SUPER_ADMIN_DELETE])}
          >
            <UnarchiveOutlinedIcon fontSize='small' />
          </IconButton>
        )}
        <IconButton
          title='Delete'
          size='small'
          onClick={() => handleDeleteDialogOpen(params?.data)}
          disabled={!KeycloakService?.hasRole([SUPER_ADMIN_DELETE])}
        >
          <DeleteOutlined fontSize='small' />
        </IconButton>
      </div>
    )
  }
  useEffect(() => {
    setTotalCount(filtered?.length || 0)
  }, [filtered])

  const paginatedData = React?.useMemo(() => {
    const start = page * pageSize
    const end = start + pageSize
    return filtered?.slice(start, end)
  }, [filtered, page, pageSize])

  const columnDefs: ColDef[] = [
    {
      headerName: 'SeqNo',
      field: 'id',
      width: 90,
      sortable: true,
      filter: false,
      headerClass: classes?.centerHeader,
      cellRenderer: (params: any) => params?.data?.sequence_number ?? '-',
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Id',
      field: 'id',
      width: 90,
      sortable: true,
      filter: false,
      headerClass: classes?.centerHeader,
      cellRenderer: (params: any) => params?.data?.id ?? '-',
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      headerComponent: CustomHeader,
    },
    {
      headerName: 'Bed Name',
      field: 'bed_number',
      minWidth: 150,
      sortable: false,
      filter: false,
      cellRenderer: renderName,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: 0.5,
      },
      flex: 1,
      wrapText: true,
    },
    {
      headerName: 'Room Name',
      field: 'bedName',
      minWidth: 160,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) =>
        params?.data?.room_detail?.room_name ?? '-',
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
        wordBreak: 'break-word',
        whiteSpace: 'normal !important',
        overflowWrap: 'break-word',
        lineHeight: 1.5,
      },
      flex: 1,
      wrapText: true,
    },
    {
      headerName: 'Bed Category',
      field: 'bed_category',
      minWidth: 110,
      sortable: true,
      filter: true,
      headerClass: classes?.centerHeader,
      cellRenderer: (params: any) =>
        params?.data?.bed_category?.name === 'critical'
          ? allocation.critical
          : allocation.nonCritical,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
      headerComponent: CustomHeader,
    },
    {
      headerName: 'floor',
      field: 'floor_number',
      minWidth: 90,
      sortable: false,
      filter: false,
      headerClass: classes?.centerHeader,
      cellRenderer: (params: any) =>
        params?.data?.room_detail.floor_number ?? emptyString,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Location',
      field: 'location_id',
      minWidth: 130,
      sortable: false,
      filter: false,
      headerClass: classes?.centerHeader,
      cellRenderer: (params: any) => params?.data?.location_id,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Actions',
      field: 'id',
      minWidth: 140,
      headerClass: classes?.centerHeader,
      cellRenderer: RenderActions,
      cellClass: 'custom-header-cell',
      cellStyle: { fontSize: '12px', textAlign: 'center' },
    },
    {
      headerName: 'Created By',
      field: 'created_by',
      minWidth: 130,
      sortable: false,
      filter: false,
      cellRenderer: createProvenanceView,
      headerClass: classes?.centerHeader,
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
        wordBreak: 'break-word',
        whiteSpace: 'normal !important',
        overflowWrap: 'break-word',
        lineHeight: 1.5,
      },
      wrapText: true,
      flex: 1,
    },
    {
      headerName: 'Updated By',
      field: 'updated_by',
      minWidth: 140,
      sortable: false,
      filter: false,
      headerClass: classes?.centerHeader,
      cellRenderer: updatedProvenanceView,
      cellClass: 'custom-header-cell',
      cellStyle: {
        fontSize: '12px',
        textAlign: 'center',
        wordBreak: 'break-word',
        whiteSpace: 'normal !important',
        overflowWrap: 'break-word',
        lineHeight: 1.5,
      },
      wrapText: true,
      flex: 1,
    },
  ]
  return (
    <>
      <Box height={'100%'} width={'100%'}>
        <MedAgDataGrid
          tableHeight={'100%'}
          rowData={paginatedData}
          columnDefs={columnDefs}
          pagination={true}
          pageSizeOptions={pageSizeOptions}
          exportIcon={false}
          searchField={false}
          headerHeight={30}
          rowHeight={40}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalRows={totalCount}
          localSearch={false}
          enableCellTextSelection={true}
          loading={status === loading ? true : false}
        />
      </Box>
      {deleteDialog && (
        <DeleteConfirmationDialog
          openDialog={deleteDialog}
          closeDialog={handleCloseDeleteDialog}
          data={bedData}
          name={bedData?.bed_number}
          setData={setTableData}
          item={menuValues?.Bed}
        />
      )}
    </>
  )
}

export default Beds
